import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import ListingStatsCard from "../components/ListingStatsCard";
import PublicLinkError from "../components/PublicLinkError";
import ReelReactions from "../components/ReelReactions";
import ReelChat from "../components/ReelChat";
import ReelCTAs from "../components/ReelCTAs";
import LiveViewerBadge from "../components/LiveViewerBadge";
import ShareRow from "../components/ShareRow";
import EmbedSnippet from "../components/EmbedSnippet";
import DocumentsAccordion from "../components/DocumentsAccordion";
import OtherListingsStrip from "../components/OtherListingsStrip";
import RealtorChatbotBubble from "../components/RealtorChatbotBubble";
import { PUBLIC_BASE, fetchPublicWithRetry } from "../lib/api";

const BACKEND_URL = PUBLIC_BASE;

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null); // null | "transient" | "not_found"
  const [retrying, setRetrying] = useState(false);
  const videoRef = useRef(null);
  const lastBucketRef = useRef(-1);

  const loadShare = (opts = {}) => {
    setErr(null);
    setRetrying(opts.fromUserClick === true);
    return fetchPublicWithRetry(`${BACKEND_URL}/api/share/reels/${token}`)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        setErr(e?.kind === "not_found" ? "not_found" : "transient");
      })
      .finally(() => setRetrying(false));
  };

  useEffect(() => {
    loadShare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Progress tracking — ping backend as viewer crosses each 10% bucket
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !data?.video_url) return;
    lastBucketRef.current = -1;
    const onTime = () => {
      if (!v.duration || !isFinite(v.duration)) return;
      const pct = (v.currentTime / v.duration) * 100;
      const bucket = Math.min(9, Math.floor(pct / 10));
      if (bucket > lastBucketRef.current) {
        lastBucketRef.current = bucket;
        // Fire-and-forget; viewer shouldn't care about the response
        axios
          .post(`${BACKEND_URL}/api/share/reels/${token}/progress`, {
            percent: bucket * 10,
          })
          .catch(() => {});
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [token, data?.video_url]);

  if (err) {
    return (
      <PublicLinkError
        kind={err}
        retrying={retrying}
        onRetry={() => loadShare({ fromUserClick: true })}
        testId="share-page-invalid"
        context="this reel"
      />
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Icons.Loader2 size={20} className="text-zinc-600 animate-spin" />
      </div>
    );
  }

  const realtor = data.realtor || {};
  const brand = data.brand_color;
  // Inline style tokens driven by the company's brand color (or a neutral fallback)
  const accent = brand || "#FFFFFF";
  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{ "--brand": accent }}
    >
      {/* Top brand bar — subtle accent line */}
      {brand && (
        <div className="h-1 w-full" style={{ background: brand }} />
      )}

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div
          className="text-[10px] tracking-[0.4em] uppercase mb-2"
          style={{ color: brand || "#71717a" }}
        >
          Listing Reel
        </div>
        <div className="font-display text-3xl text-white mb-1">
          {data.title}
        </div>
        {data.listing_address && (
          <div className="text-zinc-400 text-sm mb-6">{data.listing_address}</div>
        )}

        {data.video_url ? (
          <video
            ref={videoRef}
            src={`${BACKEND_URL}${data.video_url}`}
            controls
            autoPlay
            className="w-full bg-black rounded-2xl mb-6"
            style={
              brand
                ? {
                    boxShadow: `0 0 0 2px ${brand}22, 0 10px 40px ${brand}10`,
                  }
                : undefined
            }
          />
        ) : (
          <div className="aspect-video rounded-2xl bg-black/60 border border-white/10 flex items-center justify-center text-zinc-500">
            Reel still rendering — refresh in a moment.
          </div>
        )}

        {/* Live viewer count + share row — both lightweight, sit beneath the video */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <LiveViewerBadge kind="reel" token={data.share_token} brand={brand} />
          <div className="ml-auto flex items-center gap-2">
            <EmbedSnippet
              kind="reel"
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={data.title || data.listing_address}
              brand={brand}
            />
            <ShareRow
              title={data.title}
              subtitle={data.listing_address}
              brand={brand}
            />
          </div>
        </div>

        {/* Frictionless reactions */}
        <ReelReactions shareToken={data.share_token} brand={brand} />

        {/* Q&A thread — primary contact channel.  Removed the
            Book/Floorplan/Offer CTA strip after realtor feedback: chat
            already handles every contact intent, and the docs accordion
            below already offers floor-plan download — three more buttons
            duplicated those flows.  Slot picker is still available via
            chat or by re-introducing a single Book CTA if needed. */}
        <ReelChat shareToken={data.share_token} brand={brand} />

        {/* Property documents (floor plan, brochure, etc.) — auto-hides when empty */}
        <DocumentsAccordion listingId={data.listing_id} brand={brand} />

        {/* Listing stats card — renders below the video if the listing has any stats */}
        <div className="mt-6">
          <ListingStatsCard
            stats={data.listing_stats}
            brand={brand}
            data-testid="share-stats-card"
          />
        </div>

        {/* Realtor's other current listings — visual cross-promotion */}
        <div className="mt-8">
          <OtherListingsStrip
            listingId={data.listing_id}
            brand={brand}
            title="More from this agent"
          />
        </div>

        {data.listing_url && (
          <a
            href={data.listing_url}
            target="_blank"
            rel="noreferrer"
            className="group mb-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all"
            style={{
              background: brand || "#ffffff",
              color: brand ? "#050505" : "#050505",
              boxShadow: brand ? `0 10px 30px ${brand}30` : "none",
            }}
            data-testid="share-view-full-listing"
          >
            <Icons.ExternalLink size={16} />
            View full listing
            <Icons.ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
        )}

        {realtor.name && (
          <div
            className="bg-[#0A0A0A] border rounded-2xl p-5 flex items-center gap-4"
            style={{ borderColor: brand ? `${brand}40` : "rgb(255 255 255 / 0.1)" }}
          >
            {realtor.headshot_url && (
              <img
                src={`${BACKEND_URL}${realtor.headshot_url}`}
                alt=""
                className="w-16 h-16 rounded-full object-cover shrink-0"
                style={
                  brand
                    ? { boxShadow: `0 0 0 3px ${brand}` }
                    : undefined
                }
              />
            )}
            <div className="flex-1 min-w-0">
              <div
                className="text-xs uppercase tracking-widest"
                style={{ color: brand || "#71717a" }}
              >
                Presented by
              </div>
              <div className="font-display text-xl text-white mt-0.5 truncate">
                {realtor.name}
              </div>
              {realtor.brokerage_name && (
                <div className="text-sm text-zinc-400">
                  {realtor.brokerage_name}
                </div>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400 mt-2">
                {realtor.phone && <span>{realtor.phone}</span>}
                {realtor.email && <span>{realtor.email}</span>}
                {realtor.website && (
                  <a
                    href={realtor.website}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-white"
                    style={brand ? { color: brand } : undefined}
                  >
                    {realtor.website}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LeadCapture is only useful when an open-house RSVP is needed —
            chat above handles every other contact intent. */}
        {data.open_house && (data.open_house.date || data.open_house.time) && (
          <LeadCapture
            shareToken={data.share_token}
            brand={brand}
            openHouse={data.open_house}
            listingAddress={data.listing_address}
          />
        )}
      </div>

      {/* Realtor's AI chatbot — floating bubble, lazy-loaded.
          Renders nothing when the realtor hasn't configured a URL.
          Context tells the bot exactly which listing the viewer is on
          so its greeting can reference this property by name. */}
      <RealtorChatbotBubble
        url={data.realtor?.chatbot_embed_url}
        realtorName={data.realtor?.name}
        brandColor={brand}
        context={{
          page: "share",
          share_token: data.share_token,
          listing_address: data.listing_address,
          listing_url: data.listing_url,
          listing_price: data.listing_stats?.price,
          listing_currency: data.listing_stats?.currency,
          listing_beds: data.listing_stats?.bedrooms,
          listing_baths: data.listing_stats?.bathrooms,
          listing_sqft: data.listing_stats?.sqft,
          realtor_name: data.realtor?.name,
          realtor_phone: data.realtor?.phone,
          realtor_email: data.realtor?.email,
        }}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Lead capture block — shown below the realtor card on every share page.
// Two tabs: quick email-gate ("get the floor plan"), and full open-house
// RSVP (only shown when the listing has an open house scheduled).
// Both flow into the realtor's Hot Lead Inbox on the dashboard.
// ────────────────────────────────────────────────────────────────────────
function LeadCapture({ shareToken, brand, openHouse, listingAddress }) {
  // Only used now when an open house is scheduled — pure RSVP form.
  // The "Ask a question" tab was removed since the dedicated ReelChat
  // component above already handles questions, and the standalone "Get
  // the photo + floor plan pack" tab was removed because the floor-plan
  // doc is shown inline on this same page.
  const [tab] = useState("rsvp");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  if (!shareToken) return null;

  const submit = async () => {
    setErr(null);
    setSending(true);
    try {
      const body =
        tab === "rsvp"
          ? {
              share_token: shareToken,
              source: "open_house_rsvp",
              name: name || null,
              email: email || null,
              phone: phone || null,
              party_size: Number(partySize) || null,
              message: message || null,
            }
          : {
              share_token: shareToken,
              source: "question",
              name: name || null,
              email: email || null,
              phone: phone || null,
              message,
            };
      await axios.post(`${BACKEND_URL}/api/public/leads`, body);
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="mt-8 bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 text-center"
        data-testid="lead-capture-thanks"
      >
        <Icons.CheckCircle2
          size={24}
          className="mx-auto mb-2"
          style={{ color: brand || "#22c55e" }}
        />
        <div className="font-display text-lg text-white">Thank you!</div>
        <div className="text-[12px] text-zinc-400 mt-1">
          {tab === "rsvp"
            ? "Your RSVP was sent to the agent — they'll be in touch before the open house."
            : "The agent will be in touch shortly."}
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-8 bg-[#0A0A0A] border border-white/10 rounded-2xl p-5 sm:p-6"
      data-testid="lead-capture"
    >
      {/* Headline — RSVP-only since chat handles general questions */}
      <div className="mb-4 flex items-center gap-2">
        <Icons.CalendarCheck size={14} style={{ color: brand || "#a1a1aa" }} />
        <div>
          <div className="font-display text-xl text-white">
            Reserve your slot{openHouse?.date ? ` for ${openHouse.date}` : ""}
          </div>
          <div className="text-[12px] text-zinc-500 mt-0.5">
            The agent will confirm by email{openHouse?.time ? ` ahead of ${openHouse.time}` : ""}.
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-2.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <input
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            data-testid="lead-name"
          />
          <input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            data-testid="lead-email"
          />
        </div>
        {(tab === "rsvp" || tab === "ask") && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <input
              placeholder="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              data-testid="lead-phone"
            />
            {tab === "rsvp" && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-zinc-500 shrink-0">Party of</span>
                <select
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
                  data-testid="lead-party-size"
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n} className="bg-black">
                      {n} {n === 1 ? "person" : "people"}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        {tab === "ask" && (
          <textarea
            placeholder="Your question…"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            data-testid="lead-message"
          />
        )}
        {err && (
          <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2">
            {err}
          </div>
        )}
        <button
          onClick={submit}
          disabled={
            sending ||
            (tab === "rsvp" && !email && !phone) ||
            (tab === "ask" && !message.trim())
          }
          className="w-full font-medium rounded-lg py-2.5 disabled:opacity-30 flex items-center justify-center gap-2 transition-all"
          style={{
            background: brand || "#ffffff",
            color: brand ? "#ffffff" : "#000000",
          }}
          data-testid="lead-submit"
        >
          {sending ? (
            <Icons.Loader2 size={14} className="animate-spin" />
          ) : (
            <Icons.Send size={14} />
          )}
          {tab === "rsvp" ? "Reserve my slot" : "Send question"}
        </button>
        <div className="text-[10px] text-zinc-600 text-center">
          {listingAddress ? `Re: ${listingAddress}` : "Private · shared only with the listing agent"}
        </div>
      </div>
    </div>
  );
}
